import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { addSyncListener, removeSyncListener, getSyncStatus, startManualSync, stopSync } from '../../utils/backgroundSync';
import styles from './SyncProgress.module.css';

/**
 * 동기화 진행률 상세 표시 컴포넌트
 * - 실시간 동기화 진행 상황 표시
 * - 개별 레포트 동기화 상태 표시
 * - 수동 동기화 시작/중단 버튼
 * - 이미지 업로드 진행 상태 표시
 */
const SyncProgress = ({ isVisible, onClose, pendingCount, isRunning }) => {
  const { t } = useTranslation();

  const [syncState, setSyncState] = useState({
    isRunning: false,
    progress: null,
    currentReport: null,
    imageUpload: null, // 이미지 업로드 진행 상태
    syncCompleted: false, // 🆕 동기화 완료 상태
    completedCount: 0, // 🆕 완료된 개수
    failedCount: 0, // 🆕 실패한 개수
    totalCount: 0 // 🆕 동기화 대상 전체 개수
  });

  // 동기화 상태 변화 핸들러
  const handleSyncChange = (event, data) => {
    // console.log('[SyncProgress] 동기화 이벤트:', event, data);

    setSyncState(prevState => {
      const newState = { ...prevState };

      switch (event) {
        case 'auth_required':
          // 🆕 인증 필요 이벤트 처리 - SyncProgress 모달에서는 리스너만 업데이트
          // 실제 알림과 페이지 이동은 OfflineIndicator에서 처리
          newState.isRunning = false;
          newState.currentReport = null;
          newState.imageUpload = null;
          break;

        case 'sync_started':
          newState.isRunning = true;
          newState.progress = data;
          newState.syncCompleted = false; // 동기화 시작 시 완료 상태 초기화
          break;

        case 'sync_progress':
          newState.progress = data;
          if (data.current) {
            newState.currentReport = data.current;
          }
          break;

        case 'sync_completed':
          newState.isRunning = false;
          newState.currentReport = null;
          newState.imageUpload = null;
          // 🆕 동기화 완료 시 완료 상태 및 개수 저장
          // 실패/미전송 건이 있으면 완료 화면 대신 경고 화면을 표시하기 위해 함께 저장
          newState.syncCompleted = true;
          newState.completedCount = data.completed || 0;
          newState.failedCount = data.failed || 0;
          newState.totalCount = data.total || 0;
          break;

        case 'sync_stopped':
          newState.isRunning = false;
          newState.currentReport = null;
          newState.imageUpload = null;
          break;

        case 'image_upload_started':
          newState.imageUpload = {
            reportId: data.reportId,
            reportName: data.reportName,
            totalImages: data.totalImages,
            currentImage: 0,
            fileName: null,
            status: 'started'
          };
          break;

        case 'image_upload_progress':
          newState.imageUpload = {
            reportId: data.reportId,
            reportName: data.reportName,
            totalImages: data.totalImages,
            currentImage: data.currentImage,
            fileName: data.fileName,
            status: data.status,
            compressedSize: data.compressedSize,
            originalSize: data.originalSize,
            compressionRate: data.compressionRate,
            error: data.error
          };
          break;

        case 'image_upload_completed':
          newState.imageUpload = null;
          break;

        default:
          break;
      }

      return newState;
    });
  };

  // 수동 동기화 시작
  const handleManualSync = async () => {
    try {
      await startManualSync();
    } catch (error) {
      // console.error('[SyncProgress] 수동 동기화 시작 실패:', error);
    }
  };

  // 동기화 중단
  const handleStopSync = () => {
    stopSync();
  };

  // 컴포넌트 초기화
  useEffect(() => {
    if (isVisible) {
      // 동기화 리스너 등록
      addSyncListener(handleSyncChange);

      // 현재 동기화 상태 가져오기
      const currentStatus = getSyncStatus();
      setSyncState(prevState => ({
        ...prevState,
        isRunning: currentStatus.isRunning,
        progress: currentStatus.progress,
        currentReport: currentStatus.currentReport
      }));

      return () => {
        removeSyncListener(handleSyncChange);
      };
    }
  }, [isVisible]);

  // 🆕 pendingCount가 증가하면 완료 상태 초기화
  // 단, 미전송 경고 화면은 사용자가 확인해야 하므로 유지
  useEffect(() => {
    if (pendingCount > 0) {
      setSyncState(prevState => {
        if (prevState.syncCompleted && prevState.failedCount > 0) {
          return prevState;
        }
        return {
          ...prevState,
          syncCompleted: false,
          completedCount: 0,
          failedCount: 0,
          totalCount: 0
        };
      });
    }
  }, [pendingCount]);

  if (!isVisible) {
    return null;
  }

  const { progress } = syncState;
  const progressPercentage = progress ? (progress.completed / progress.total) * 100 : 0;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h5 className={styles.title}>
            🔄 {t('Sync Progress')}
          </h5>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className={styles.content}>
          {/* 동기화 상태 요약 */}
          <div className={styles.statusSection}>
            {syncState.isRunning ? (
              <div className={styles.syncingStatus}>
                <div className={styles.statusText}>
                  <strong>{t('Syncing in progress')}</strong>
                  {progress && (
                    <span className={styles.statusDetail}>
                      {progress.completed}/{progress.total} {t('completed')}
                    </span>
                  )}
                </div>

                {/* 프로그레스 바 */}
                {progress && (
                  <div className={styles.progressContainer}>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${progressPercentage}%` }}
                      />
                    </div>
                    <span className={styles.progressText}>
                      {progressPercentage.toFixed(0)}%
                    </span>
                  </div>
                )}

                {/* 현재 처리 중인 레포트 */}
                {syncState.currentReport && (
                  <div className={styles.currentReport}>
                    <span className={styles.currentLabel}>{t('Current')}:</span>
                    <span className={styles.currentName}>
                      {syncState.currentReport.name}
                    </span>
                  </div>
                )}

                {/* 🆕 이미지 업로드 진행률 */}
                {syncState.imageUpload && (
                  <div className={styles.imageUploadSection}>
                    <div className={styles.imageUploadHeader}>
                      <span className={styles.imageUploadLabel}>
                        📤 {t('Uploading images')}
                      </span>
                      <span className={styles.imageUploadCount}>
                        {syncState.imageUpload.currentImage}/{syncState.imageUpload.totalImages}
                      </span>
                    </div>

                    {/* 이미지 업로드 프로그레스 바 */}
                    <div className={styles.imageProgressContainer}>
                      <div className={styles.imageProgressBar}>
                        <div
                          className={styles.imageProgressFill}
                          style={{
                            width: `${(syncState.imageUpload.currentImage / syncState.imageUpload.totalImages) * 100}%`
                          }}
                        />
                      </div>
                      <span className={styles.imageProgressText}>
                        {((syncState.imageUpload.currentImage / syncState.imageUpload.totalImages) * 100).toFixed(0)}%
                      </span>
                    </div>

                    {/* 현재 처리 중인 이미지 */}
                    {syncState.imageUpload.fileName && (
                      <div className={styles.currentImageInfo}>
                        <div className={styles.currentImageName}>
                          {syncState.imageUpload.status === 'compressing' && '🔧 '}
                          {syncState.imageUpload.status === 'uploading' && '📤 '}
                          {syncState.imageUpload.status === 'completed' && '✅ '}
                          {syncState.imageUpload.status === 'failed' && '❌ '}
                          {syncState.imageUpload.fileName}
                        </div>
                        {syncState.imageUpload.status === 'compressing' && (
                          <div className={styles.imageStatus}>
                            {t('Compressing')}...
                          </div>
                        )}
                        {syncState.imageUpload.status === 'uploading' && (
                          <div className={styles.imageStatus}>
                            {t('Uploading')}
                            {syncState.imageUpload.compressedSize &&
                              ` (${(syncState.imageUpload.compressedSize / 1024 / 1024).toFixed(2)}MB)`
                            }
                            {syncState.imageUpload.compressionRate && syncState.imageUpload.compressionRate !== '압축 안됨' &&
                              ` - ${t('Compressed')} ${syncState.imageUpload.compressionRate}`
                            }
                          </div>
                        )}
                        {syncState.imageUpload.status === 'failed' && syncState.imageUpload.error && (
                          <div className={styles.imageError}>
                            {t('Error')}: {syncState.imageUpload.error}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* 중단 버튼 */}
                <button
                  className={`${styles.button} ${styles.stopButton}`}
                  onClick={handleStopSync}
                >
                  ⏹️ {t('Stop Sync')}
                </button>
              </div>
            ) : (
              <div className={styles.idleStatus}>
                {/* 🆕 동기화 완료 직후 */}
                {syncState.syncCompleted ? (
                  syncState.failedCount > 0 || syncState.completedCount < syncState.totalCount ? (
                    /* 🆕 일부 전송 실패 - 완료로 표시하지 않고 미전송 잔여 건수를 명시 */
                    <div className={styles.failedStatus}>
                      <div className={styles.statusText}>
                        <strong>⚠️ {t('Sync finished with unsent reports')}</strong>
                      </div>
                      <div className={styles.failedMessage}>
                        <span>
                          {syncState.totalCount - syncState.completedCount}{t('reports remain unsent on this device')}
                        </span>
                      </div>
                      {syncState.completedCount > 0 && (
                        <div className={styles.completedMessage}>
                          <span>
                            {syncState.completedCount}{t('reports successfully sent to server')}
                          </span>
                        </div>
                      )}
                      <div className={styles.failedHint}>
                        {t('Please retry sync in a better network environment')}
                      </div>
                      <button
                        className={`${styles.button} ${styles.startButton}`}
                        onClick={handleManualSync}
                      >
                        🔄 {t('Retry Sync')}
                      </button>
                    </div>
                  ) : (
                    <div className={styles.completedStatus}>
                      <div className={styles.statusText}>
                        <strong>✅ {t('Sync completed successfully')}</strong>
                      </div>
                      <div className={styles.completedMessage}>
                        {syncState.completedCount > 0 ? (
                          <span>
                            {syncState.completedCount}{t('reports successfully sent to server')}
                          </span>
                        ) : (
                          <span>{t('All offline reports synced')}</span>
                        )}
                      </div>
                    </div>
                  )
                ) : (
                  /* 🆕 대기 상태 - 오프라인 레포트 유무 표시 */
                  <div className={styles.waitingStatus}>
                    <div className={styles.statusText}>
                      {pendingCount > 0 ? (
                        <>
                          <strong>📋 {t('Offline reports saved')}</strong>
                          <div className={styles.pendingInfo}>
                            <span className={styles.pendingCount}>{pendingCount}</span>
                            <span>{t('reports waiting to sync')}</span>
                          </div>
                        </>
                      ) : (
                        <strong>✅ {t('No offline reports')}</strong>
                      )}
                    </div>

                    {/* 수동 동기화 시작 버튼 (레포트가 있을 때만) */}
                    {pendingCount > 0 && (
                      <button
                        className={`${styles.button} ${styles.startButton}`}
                        onClick={handleManualSync}
                      >
                        🔄 {t('Start Sync')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyncProgress;